import { useQuery } from '@tanstack/react-query';
import { apiAuth } from '../auth-client';
/**
 * Dashboard stats — derived from existing endpoints because there's no
 * dedicated `/admin/users/stats` yet. Each "card" gets its own query so
 * partial failures don't black-hole the whole page.
 *
 * Counts come from `per_page=1` queries — global-auth still returns
 * `.total` in the response, just doesn't ship the rows we don't need.
 * Network footprint per dashboard load: 4 small queries (~100 bytes each)
 * + the recent-users list (10 rows). Same order of magnitude as the
 * backup-service dashboard.
 */
export function useAuthUserCount() {
    return useQuery({
        queryKey: ['auth', 'users', 'count', 'total'],
        queryFn: () => apiAuth('/admin/users?page=1&per_page=1'),
        select: (d) => d.total,
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
    });
}
export function useAuthBannedCount() {
    return useQuery({
        queryKey: ['auth', 'users', 'count', 'banned'],
        queryFn: () => apiAuth('/admin/users?page=1&per_page=1&is_banned=true'),
        select: (d) => d.total,
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
    });
}
export function useAuthAdminCount() {
    return useQuery({
        queryKey: ['auth', 'users', 'count', 'admin'],
        queryFn: () => apiAuth('/admin/users?page=1&per_page=1&role=admin'),
        select: (d) => d.total,
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
    });
}
export function useAuthRecentUsers(limit = 10) {
    return useQuery({
        queryKey: ['auth', 'users', 'recent', limit],
        queryFn: () => apiAuth(`/admin/users?page=1&per_page=${limit}`),
        // Smaller cadence — fresh registrations matter on this view.
        refetchInterval: 15_000,
        refetchIntervalInBackground: false,
    });
}
/**
 * "Users joined in the last 24h" — computed client-side from the recent
 * list. Cheap because the list is already cached for the recent-list card
 * above. NOT exact: if more than `recent_window` users joined in 24h, we
 * undercount. Acceptable for an admin-overview MVP; bump to a real stats
 * endpoint when it becomes a real number.
 */
export function deriveLast24hCount(users) {
    if (!users)
        return 0;
    const cut = Date.now() - 24 * 60 * 60 * 1000;
    return users.filter((u) => new Date(u.created_at).getTime() > cut).length;
}
// ─── Services count (used as a dashboard tile) ───────────────────────────
export function useAuthServiceCount() {
    return useQuery({
        queryKey: ['auth', 'services', 'count'],
        queryFn: async () => {
            const list = await apiAuth('/admin/services');
            return {
                total: list.length,
                active: list.filter((s) => s.is_active).length,
            };
        },
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
    });
}
